import React, { createRef, Fragment } from "react";
import classnames from "classnames";
import { SortEnd } from "react-sortable-hoc";
import { Shop, Theme } from "@/sunzi";
import { VipIncrement } from "@/increment/increment";
import {
  ImageHandleType,
  ImageListItem,
  SourceListItem,
  Type,
  IDoubleDesign,
  ICustomOutput,
} from "./spotify-code-film";
import {
  Header,
  Icon,
  Wrapper,
  Cropper,
  SortableList,
  Cropai,
  Loading,
  Layout,
} from "@/components";
import {
  i18n,
  autoCropperImage,
  autoCropaiImage,
  ignore3dTouch,
  getToken7n,
  upload7nB64,
  dateTime,
  uuid,
  composeHand,
  compressImage,
  createObjectURL,
} from "@/utils";
import {
  SpotifyTrack,
  SpotifyColor,
  SpotifyLayout,
  SpotifyBottomImage,
  SpotifyBindProduct,
  SpotifyOuput,
} from "../spotify-code/spotify-code";
import Preview from "./preview";
import SpotifyCode from "../spotify-code";
import styles from "./style.less";
import GiftDrawer from "../giftdrawer";
import DoubleDrawer from "../double-drawer";
import DoubleDesign from "../double-design";

type FactoryComponent<P> =
  | (new (props: P) => React.Component)
  | ((
      props: P & { children?: React.ReactNode }
    ) => React.ReactElement<P> | null);

export interface MultiPhotoProps {
  /** classname */
  className?: string;
  Content: FactoryComponent<MultiPhotoContentProps>;
  PreviewContent: FactoryComponent<MultiPhotoPreviewProps>;
  /** 店铺信息 */
  shop: Shop;
  /** 原图处理类型 */
  handleType: ImageHandleType;
  /** 是否需要合成图 */
  needCompose?: boolean;
  /** 最小传图数量 */
  minCount?: number;
  /** 外置更换图片方法 */
  onExternalCameraChange?: () => Promise<string>;
  /** 用户上传的原图 */
  sources: string[];
  /** image数组定制参数 */
  imageList: ImageListItem[];
  /** 是否支持排序 */
  supportSort?: boolean;
  /** 胶卷样式 */
  filmStyle: string;
  /** ga对象 */
  gtag?: any;
  /** 关闭回调 */
  onClose: () => void;
  /** 确认回调 */
  onConfirm: (
    output: (ICustomOutput | undefined)[],
    vipIncrement?: VipIncrement
  ) => void;
  /** 加车附带时间 */
  onAddCart?: (output?: ICustomOutput) => Promise<void>;
  /**加价 */
  addPrice: string;
  /** 推荐歌曲 */
  recommendTracks?: SpotifyTrack[];
  /** 礼物盒 */
  giftBoxs?: Type[];
  /** 第二件八折 */
  doubleDesign?: IDoubleDesign;
}

export interface MultiPhotoState {
  /** 加载状态 */
  loading: boolean;
  /** 维护当前photo信息 */
  sourceList: SourceListItem[];
  /** 裁剪显隐 */
  cropperVisible: boolean;
  /** cropai显隐 */
  cropaiVisible: boolean;
  /** 预览显示 */
  showPreview: boolean;
  /** 排序列表选择的索引 */
  activeIndex: number;
  /** 提示信息 */
  tipsType: "count" | "sort";
  /** 上传进度 */
  uploadPercent: number;
  /** vip弹层显隐 */
  vipIncrementVisible?: boolean;
  /**Add Spotify Code页的显隐 */
  selectPage: boolean;
  /** 当前激活的歌曲 */
  currentTrack: SpotifyTrack;
  /**spotify-code生成图 */
  codeImg?: string;
  /**是否显示Spotify页面 */
  showSpotify: boolean;
  toSpotifyCart: boolean;
  /**照片上传失败 */
  uploadError: boolean;
  /**重置动画 */
  reStart: boolean;
  /** 礼盒弹窗显影 */
  showGiftDrawer: boolean;
  /** 双面定制弹窗 */
  showDoubleDrawer: boolean;
  /** VIP关联产品 */
  vipIncrement?: VipIncrement;
  /** 礼盒关联产品 */
  giftBox?: Type;
  /** 第二份设计弹框 */
  doubleDesignVisible: boolean;
  /** 维护当前photo信息 */
  doubleDesignSources: string[];
}

export interface MultiPhotoContentProps {
  theme: Theme;
  activeIndex: number;
  sourceList: SourceListItem[];
}

export interface MultiPhotoPreviewProps {
  theme: Theme;
  sourceList: SourceListItem[];
  filmStyle: string;
  showSpotify?: boolean;
  selectPage?: boolean;
  onClose?: () => void;
  track: SpotifyTrack;
  getCodeimgCb?: (img: any) => void;
}

class MultiPhotoFactory extends React.Component<
  MultiPhotoProps,
  MultiPhotoState
> {
  private _cropper: React.RefObject<Cropper> = createRef();
  private _cropai: React.RefObject<Cropai> = createRef();
  private _sortable: React.RefObject<SortableList> = createRef();
  private _input: React.RefObject<HTMLInputElement> = createRef();
  private _doubleInput: React.RefObject<HTMLInputElement> = createRef();
  private _result: any[] = [];
  private _newDesign?: boolean = undefined; // 是否新设计
  constructor(props: MultiPhotoProps) {
    super(props);
    this.state = {
      loading: true,
      sourceList: [],
      activeIndex: 0,
      tipsType: "count",
      cropperVisible: false,
      cropaiVisible: false,
      showPreview: false,
      uploadPercent: 0,
      vipIncrementVisible: false,
      selectPage: false,
      showSpotify: false,
      toSpotifyCart: false,
      currentTrack: {
        album: "",
        albumThumbnail: "",
        name: "",
        artists: "",
        duration_ms: 0,
        uri: "",
      },
      codeImg: "",
      uploadError: false,
      reStart: false,
      showGiftDrawer: false,
      showDoubleDrawer: false,
      vipIncrement: undefined,
      giftBox: undefined,
      doubleDesignVisible: false,
      doubleDesignSources: [],
    };
  }

  async componentDidMount() {
    const { shop, sources } = this.props;
    i18n.setLocal(shop.language);
    this.setState({});
    ignore3dTouch();
    await this.handleSourceImageAdd(this.props.sources);
    this.setState({
      loading: false,
    });
  }

  async componentDidUpdate(nextProps: MultiPhotoProps) {
    if (nextProps.sources !== this.props.sources) {
      this.setState({
        loading: true,
      });
      await this.handleSourceImageAdd(this.props.sources);
      this.setState({
        loading: false,
      });
    }
  }

  /** 排序结束 */
  handleSortableEnd = ({ oldIndex, newIndex }: SortEnd) => {
    const { sourceList } = this.state;
    sourceList.splice(
      oldIndex,
      1,
      ...sourceList.splice(newIndex, 1, sourceList[oldIndex])
    );
    this.setState({
      sourceList,
    });
  };

  /** 删除图片  */
  handleDelete = () => {
    const status = confirm(i18n.format("modules.global.delete.tips"));
    if (status) {
      const { sourceList, activeIndex } = this.state;
      let newActiveIndex = activeIndex;
      if (activeIndex == sourceList.length - 1) {
        newActiveIndex = activeIndex - 1;
      }
      sourceList.splice(activeIndex, 1);
      this.setState({
        sourceList,
        tipsType: "count",
        activeIndex: newActiveIndex,
      });
    }
  };

  /** 点击裁剪 */
  handleCropperClick = () => {
    const { handleType } = this.props;
    if (handleType === ImageHandleType.cropper)
      // 如果是裁剪
      this.setState({
        cropperVisible: true,
      });
    else
      this.setState({
        cropaiVisible: true,
      });
  };

  /** 裁剪关闭 */
  handleCropperClosed = () => {
    this.setState({
      cropperVisible: false,
    });
  };

  /** 裁剪确认 */
  handleCropperConfirm = () => {
    const { sourceList, activeIndex } = this.state;
    if (this._cropper.current) {
      const result = this._cropper.current.onConfirm();
      if (result) {
        sourceList[activeIndex].handle = result;
        this.setState({
          sourceList,
          cropperVisible: false,
        });
      }
    }
  };

  /** cropai确认 */
  handleCropaiConfirm = async () => {
    const { sourceList, activeIndex } = this.state;
    if (this._cropai.current) {
      const { crop, ai } = await this._cropai.current.onConfirm();
      if (crop) {
        sourceList[activeIndex].handle = crop;
        sourceList[activeIndex].ai = ai;
        this.setState({
          sourceList,
          cropaiVisible: false,
        });
      }
    }
  };

  /** cropai关闭 */
  handleCropaiClosed = () => {
    this.setState({
      cropaiVisible: false,
    });
  };

  /** 改变列表的激活下标 */
  handleSortableChange = (index: number) => {
    this.setState({
      activeIndex: index,
    });
  };

  /** 排序按钮点击 */
  handleSortableClick = () => {
    const { tipsType } = this.state;
    this.setState({
      tipsType: tipsType === "count" ? "sort" : "count",
    });
  };

  /** 批量增加图片 */
  handleSourceImageAdd = async (images: string[]) => {
    const { imageList, handleType } = this.props;
    const { sourceList } = this.state;
    if (sourceList.length < imageList.length) {
      // 并行对图片进行自动处理
      const result = await Promise.all(
        images
          .slice(0, imageList.length - sourceList.length)
          .map(async (item, index) => {
            const { width, height, cropImage, aiImage, type } =
              imageList[sourceList.length + index];
            if (handleType === ImageHandleType.cropper)
              // 如果是cropper
              return {
                source: item,
                handle: await autoCropperImage(item, width, height),
                width,
                height,
                cropImage,
                aiImage,
                type,
              };
            else {
              const { crop, ai } = await autoCropaiImage(
                item,
                cropImage,
                aiImage
              );
              return {
                source: item,
                handle: crop,
                ai,
                width,
                height,
                cropImage,
                aiImage,
                type,
              };
            }
          })
      );
      const _sourceList = sourceList.concat(result);
      this.setState(
        {
          sourceList: _sourceList,
          activeIndex: _sourceList.length - 1,
          tipsType: _sourceList.length === imageList.length ? "sort" : "count",
        },
        () => {
          this.sortableScrollToLeft();
        }
      );
    } else if (sourceList.length > imageList.length) {
      this.setState(
        {
          sourceList: sourceList.slice(0, imageList.length),
          activeIndex: imageList.length - 1,
          tipsType: "sort",
        },
        () => {
          this.sortableScrollToLeft();
        }
      );
    }
  };

  /** 外挂方法更换当前图片 */
  onExternalCameraChange = async () => {
    const { onExternalCameraChange, handleType } = this.props;
    const { sourceList, activeIndex } = this.state;
    if (onExternalCameraChange) {
      const base64 = await onExternalCameraChange();
      sourceList[activeIndex].source = base64;
      if (handleType === ImageHandleType.cropper)
        this.setState({
          cropperVisible: true,
        });
      else
        this.setState({
          cropaiVisible: true,
        });
    }
  };

  /** 更换当前图片 */
  onCameraChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!!e.target.files?.length) {
      const { handleType } = this.props;
      const { sourceList, activeIndex } = this.state;
      sourceList[activeIndex].source = createObjectURL(e.target.files[0]);
      if (handleType === ImageHandleType.cropper)
        this.setState({
          cropperVisible: true,
        });
      else
        this.setState({
          cropaiVisible: true,
        });
    }
  };

  /** 渲染提示内容 */
  renderTipsContent = () => {
    const { imageList, minCount, supportSort } = this.props;
    const { tipsType, sourceList } = this.state;
    switch (tipsType) {
      case "count":
        return sourceList.length < imageList.length ? (
          <Fragment>
            {minCount ? (
              <span>
                {i18n.format("modules.photo.film.tips.count.min", minCount)}
                <b
                  className={classnames({
                    [styles.warning]: minCount
                      ? sourceList.length < minCount
                      : sourceList.length < imageList.length,
                  })}
                >
                  {sourceList.length}
                </b>
                /{imageList.length}
              </span>
            ) : (
              <span>
                {i18n.format("modules.photo.film.tips.count", imageList.length)}
                <b className={styles.warning}>{sourceList.length}</b>/
                {imageList.length}
              </span>
            )}
          </Fragment>
        ) : null;
      case "sort":
        return supportSort && i18n.format("modules.photo.film.tips.sort");
      default:
        return null;
    }
  };

  /** 排序列表滚动条滚动到最后  */
  sortableScrollToLeft = () => {
    setTimeout(() => {
      this._sortable.current?.scrollToLeft();
    }, 0);
  };

  /** 关闭vip增值服务 */
  handleIncrementClose = () => {
    this.setState({
      vipIncrementVisible: false,
    });
  };

  /** 提交预览 */
  handleSubmit = async () => {
    this.setState({
      showPreview: true,
      selectPage: true,
    });
  };

  /** 关闭预览 */
  handlePreviewClosed = () => {
    this._result = [];
    this.setState({
      showPreview: false,
      uploadPercent: 0,
      reStart: false,
    });
  };

  /**显示spotify-code页面 */
  handleCustom = () => {
    this.setState({
      showPreview: false,
      showSpotify: true,
    });
  };

  /**关闭spotify-code页面 */
  hendleCloseSpotify = () => {
    this.setState({
      selectPage: true,
      showSpotify: false,
      showPreview: true,
      uploadPercent: 0,
      reStart: false,
      uploadError: false,
    });
  };

  /**spotify-code加车 */
  hendleSpotifyCart = () => {
    this.setState({
      toSpotifyCart: true,
    });
  };

  /** 获取当前歌曲信息 */
  getCurrentTrackCb = (value: any) => {
    this.setState({
      currentTrack: value,
    });
  };

  /** 获取spotify-code生成图 */
  getCodeimgCb = (img: any) => {
    this.setState({
      codeImg: img,
    });
  };

  /**关闭加车页面 */
  handleCloseCart = () => {
    const { reStart } = this.state;
    if (!reStart) {
      this.setState({
        selectPage: true,
        reStart: true,
        uploadPercent: 0,
        uploadError: false,
      });
    }
  };

  /** 重置动画*/
  onReStart = () => {
    this.setState({
      reStart: true,
      uploadPercent: 0,
    });
  };

  /** 判断是否存在二份折扣 */
  handleConfirm = async (vipIncrement?: VipIncrement, giftBox?: Type) => {
    let doubleDesig = false;
    if (this.props.doubleDesign) doubleDesig = true;
    this.setState({
      vipIncrement,
      giftBox,
      showDoubleDrawer: doubleDesig,
    });
    if (!doubleDesig) {
      this.handleAddCart();
    }
  };

  /**
   * 二份折扣确认回调
   * @param newDesign
   */
  handleDoubleDrawerConfirm = (newDesign?: boolean) => {
    if (newDesign) {
      this._doubleInput.current?.click();
    } else {
      this.handleAddCart();
    }
  };

  /**
   * 二份折扣图片改变事件
   */
  onDoubleSourceChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const sources = Array.from({ length: files.length }).map((_, index) =>
        createObjectURL(files[index])
      );
      this.setState({
        doubleDesignSources: sources,
        doubleDesignVisible: true,
      });
    }
  };

  /** 二份折扣关闭 */
  handleDoubleDrawerClose = () => {
    this.setState({
      showDoubleDrawer: false,
    });
  };

  /** 二份折扣弹框关闭 */
  handleDoubleDesignClose = () => {
    this.setState({
      doubleDesignVisible: false,
    });
  };

  /** 加车 */
  handleAddCart = async (doubleOutput?: ICustomOutput) => {
    const { handleType, needCompose, gtag, onAddCart } = this.props;
    this.onReStart();
    const {
      sourceList,
      toSpotifyCart,
      codeImg,
      showSpotify,
      currentTrack,
      vipIncrement,
      giftBox,
    } = this.state;
    try {
      this.setState({
        selectPage: false,
        showDoubleDrawer: false,
      });
      // 上传裁剪图 + 合成图
      const token = await getToken7n();
      const encodeKey = `multi-photo/${dateTime}/${uuid()}`;
      // 上传
      const _uplod = async (item: SourceListItem, index: number) => {
        const queue = [];
        if (item.handle)
          queue.push(
            upload7nB64({
              base64: item.handle,
              token,
              key: `${encodeKey}-${index}-handle.jpg`,
            })
          );
        else queue.push(new Promise((resolve) => resolve(undefined)));
        // 如果是cropai类型 增加上传 压缩原图 和 crop图
        if (handleType === ImageHandleType.cropai) {
          queue.push(
            upload7nB64({
              // 上传原图
              base64: await compressImage(item.source, 500),
              token,
              key: `${encodeKey}-${index}-source.jpg`,
            })
          );
          if (item.ai)
            // 上传ai图
            queue.push(
              upload7nB64({
                base64: item.ai,
                token,
                key: `${encodeKey}-${index}-ai.png`,
              })
            );
        }
        const response = await Promise.all<any>(queue);
        const [handle, source, ai] = response.map(
          (item) => item && host7n["prefix-na0"] + item.key
        );
        return { handle, source, ai, type: item.type };
      };
      // 上传队列
      this._result = await Promise.all(
        sourceList.map((item, index) => _uplod(item, index))
      );
      // 合成图的临时解决方案
      if (needCompose) {
        const compose = await composeHand(
          sourceList.map((item) => item.handle || item.source),
          100
        );
        const { key } = await upload7nB64({
          base64: compose,
          token,
          key: `${encodeKey}-compose.jpg`,
        });
        this._result.push(host7n["prefix-na0"] + key);
      }

      let _compose;
      if (needCompose) _compose = this._result.pop();
      let spotifyCodeStart = undefined;
      if (toSpotifyCart) {
        const { codeImg } = this.state;
        spotifyCodeStart = codeImg;
      }
      const output: ICustomOutput = {
        images: this._result,
        compose: _compose,
        spotifyTrack: currentTrack,
        spotifyCodeStart,
        giftBox,
      };
      const customOutputs = [output, doubleOutput];
      if (this._newDesign === false) customOutputs[1] = output;
      if (onAddCart) {
        onAddCart(output);
        return;
      }
      this.props.onConfirm(customOutputs, vipIncrement);
      this.setState({
        sourceList: [],
        uploadPercent: 0,
        showPreview: false,
        showSpotify: false,
        toSpotifyCart: false,
        reStart: false,
        codeImg: undefined,
        uploadError: false,
        doubleDesignVisible: false,
        showDoubleDrawer: false,
      });
      this.handlePreviewClosed();
    } catch (err) {
      this.setState({
        uploadError: true,
      });
      setTimeout(() => {
        this.setState({
          reStart: false,
        });
      }, 8000);
      gtag &&
        gtag("event", "error_upload", {
          event_label: (err as any).message,
        });
    }
    return;
  };

  // 关闭礼盒弹窗
  handleClose = () => {
    this.setState({ showGiftDrawer: false });
  };

  render() {
    const {
      shop,
      addPrice,
      recommendTracks,
      className,
      onConfirm,
      onClose,
      gtag,
      imageList,
      handleType,
      minCount,
      filmStyle,
      supportSort,
      onExternalCameraChange,
      giftBoxs,
      doubleDesign,
      Content,
      PreviewContent,
    } = this.props;
    const {
      loading,
      reStart,
      uploadError,
      sourceList,
      currentTrack,
      cropperVisible,
      cropaiVisible,
      showPreview,
      showSpotify,
      activeIndex,
      uploadPercent,
      selectPage,
      showGiftDrawer,
      showDoubleDrawer,
      doubleDesignVisible,
      doubleDesignSources,
    } = this.state;
    const activeSource = sourceList[activeIndex];
    let sortListData = sourceList.map((item) => item.handle || item.source);
    let theme = Theme.dark;
    if (handleType === ImageHandleType.cropai) {
      sortListData = sourceList.map((item) => item.source);
      theme = Theme.light;
    }

    return (
      <Layout plugins={[]}>
        <div
          className={classnames("sunzi-multi-photo", className, {
            "sunzi-multi-photo-light": handleType === ImageHandleType.cropai,
          })}
        >
          <Header
            closeIconColor="#000"
            onClose={onClose}
            renderContent={
              <div className={styles.headerContent}>
                {i18n.format("modules.photo.film.title")}
              </div>
            }
            renderSubmit={
              <div
                className={classnames(styles.headerSubmitIcon, {
                  [styles.headerSubmitDisabled]: minCount
                    ? sourceList.length < minCount
                    : sourceList.length < imageList.length,
                })}
                onClick={this.handleSubmit}
              >
                <Icon type="confirm" size={30} />
              </div>
            }
          />
          <div className={styles.multiPhotoContent}>
            <div className={styles.contentWrapper}>
              <Content
                theme={theme}
                activeIndex={activeIndex}
                sourceList={sourceList}
              />
            </div>
            <div className={styles.contentBottom}>
              <div className={styles.operation}>
                <div
                  className={styles.iconWrapper}
                  onClick={this.onExternalCameraChange}
                >
                  <Icon type="camera" size={30} color="#fff" />
                  <span>{i18n.format("modules.photo.film.change")}</span>
                  {!onExternalCameraChange && (
                    <input
                      ref={this._input}
                      type="file"
                      accept="image/*"
                      onChange={this.onCameraChange}
                    />
                  )}
                </div>
                <div
                  className={styles.iconWrapper}
                  onClick={this.handleCropperClick}
                >
                  <Icon type="crop" size={30} color="#fff" />
                  <span>{i18n.format("modules.photo.film.cropper")}</span>
                </div>
                {supportSort && (
                  <div
                    className={styles.iconWrapper}
                    onClick={this.handleSortableClick}
                  >
                    <Icon type="sort" size={30} color="#fff" />
                    <span>{i18n.format("modules.photo.film.sort")}</span>
                  </div>
                )}
                {minCount && minCount < sourceList.length && (
                  <div
                    className={styles.iconWrapper}
                    onClick={this.handleDelete}
                  >
                    <Icon type="delete-wide" size={30} color="#fff" />
                    <span>{i18n.format("modules.photo.film.delete")}</span>
                  </div>
                )}
              </div>
              <div className={styles.tips}>{this.renderTipsContent()}</div>
            </div>
          </div>
          <div className={styles.multiPhotoFooter}>
            <SortableList
              ref={this._sortable}
              multiple
              theme={theme}
              disabled={!supportSort}
              dataSource={sortListData}
              onCameraChange={this.handleSourceImageAdd}
              onExternalCameraChange={onExternalCameraChange}
              showAddButton={sourceList.length < imageList.length}
              activeIndex={activeIndex}
              onSortChange={this.handleSortableChange}
              onSortEnd={this.handleSortableEnd}
            />
          </div>
          {showPreview && (
            <Wrapper
              className={classnames(styles.preview, {
                [styles.bgDefault]: !selectPage,
                [styles.bgGreen]: !selectPage && filmStyle == "green",
              })}
            >
              <Preview
                onClose={this.handlePreviewClosed}
                onConfirm={this.handleConfirm}
                onCustom={this.handleCustom}
                selectPage={selectPage}
                addPrice={addPrice}
                currencySymbol={shop.currencySymbol}
                showSpotify={showSpotify}
                filmStyle={filmStyle}
                handleCloseCart={this.handleCloseCart}
                uploadError={uploadError}
                reStart={reStart}
                percent={uploadPercent}
                shop={shop}
                giftBoxs={giftBoxs}
                renderContent={
                  <PreviewContent
                    theme={theme}
                    track={currentTrack}
                    sourceList={sourceList}
                    filmStyle={filmStyle}
                  />
                }
              />
            </Wrapper>
          )}
          {cropperVisible && (
            <Wrapper>
              <Cropper
                ref={this._cropper}
                gtag={gtag}
                theme={Theme.light}
                source={activeSource.source}
                width={activeSource.width}
                height={activeSource.height}
                renderHeader={
                  <Header
                    closeIconColor="#000"
                    renderContent={
                      <div className={styles.headerContent}>
                        {i18n.format("components.cropper.title")}
                      </div>
                    }
                    onClose={this.handleCropperClosed}
                    renderSubmit={
                      <div
                        className={styles.headerSubmitIcon}
                        onClick={this.handleCropperConfirm}
                      >
                        <Icon type="confirm" size={30} />
                      </div>
                    }
                  />
                }
              />
            </Wrapper>
          )}
          {cropaiVisible && (
            <Wrapper>
              <Cropai
                ref={this._cropai}
                source={activeSource.source}
                cropImage={activeSource.cropImage}
                aiImage={activeSource.aiImage}
                gtag={gtag}
                renderHeader={
                  <Header
                    closeIconColor="#000"
                    renderContent={
                      <div className={styles.headerContent}>
                        {i18n.format("components.cropper.title")}
                      </div>
                    }
                    onClose={this.handleCropaiClosed}
                    renderSubmit={
                      <div
                        className={styles.headerSubmitIcon}
                        onClick={this.handleCropaiConfirm}
                      >
                        <Icon type="confirm" size={30} />
                      </div>
                    }
                  />
                }
              />
            </Wrapper>
          )}
          {showSpotify && (
            <Wrapper className={styles.spotifyCodePage}>
              <SpotifyCode
                shop={shop}
                gtag={gtag}
                recommendTracks={recommendTracks}
                handleConfirm={this.handleConfirm}
                filmStyle={filmStyle}
                hendleCloseSpotify={this.hendleCloseSpotify}
                hendleSpotifyCart={this.hendleSpotifyCart}
                getCurrentTrackCb={this.getCurrentTrackCb}
                uploadError={uploadError}
                showSpotify={showSpotify}
                reStart={reStart}
                percent={uploadPercent}
                sourceList={sourceList}
                giftBoxs={giftBoxs}
                // 胶卷
                _renderContent={
                  <PreviewContent
                    track={currentTrack}
                    getCodeimgCb={this.getCodeimgCb}
                    showSpotify={showSpotify}
                    theme={theme}
                    sourceList={sourceList}
                    filmStyle={filmStyle}
                  />
                }
              />
            </Wrapper>
          )}
          {loading && (
            <Wrapper className={styles.loading}>
              <Loading />
            </Wrapper>
          )}

          {/* 礼盒弹窗 */}
          {/* {showGiftDrawer && (
              <Wrapper>
                <GiftDrawer
                  shop={shop!}
                  giftBoxs={giftBoxs}
                  mask
                  currencySymbol={shop!.currencySymbol}
                  onClose={this.handleClose}
                  onConfirm={this.handleConfirm}
                />
              </Wrapper>
            )} */}

          {showDoubleDrawer && doubleDesign && (
            <DoubleDrawer
              doubleDesign={doubleDesign}
              onClose={this.handleDoubleDrawerClose}
              onConfirm={this.handleDoubleDrawerConfirm}
            />
          )}

          {doubleDesign && (
            <input
              style={{ display: "none" }}
              ref={this._doubleInput}
              type="file"
              accept="image/*"
              multiple
              onChange={this.onDoubleSourceChange}
            />
          )}

          {doubleDesignVisible && (
            <DoubleDesign
              {...this.props}
              sources={doubleDesignSources}
              doubleDesign={undefined}
              onClose={this.handleDoubleDesignClose}
              onAddCart={this.handleAddCart}
            />
            // <DoubleDesign>
            //   <SpotifyCodeFilm
            //     {...this.props}
            //     sources={doubleDesignSources}
            //     doubleDesign={undefined}
            //     onClose={this.handleDoubleDesignClose}
            //     onAddCart={this.handleAddCart}
            //   />
            // </DoubleDesign>
          )}
        </div>
      </Layout>
    );
  }
}

export {
  ImageListItem,
  SourceListItem,
  ImageHandleType,
  ImageListCropaiItem,
  ImageListCropperItem,
} from "./spotify-code-film";

export default MultiPhotoFactory;
